/**
 * Profile Favourites Route
 */
import { createFileRoute } from '@tanstack/react-router';
import { ListPageTemplatePending } from '@/components/v2/page-template-pending';
import { favouritesQuery } from '@/lib/queries/detail';
import { cacheFirst } from '@/lib/queries/cache-first';

export const Route = createFileRoute('/_authenticated/profile/favourites')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The loaded page hardcodes this exact title and opens on the Studios |
  // Instructors switcher, so the pending renders them as settled chrome.
  pendingComponent: () => (
    <ListPageTemplatePending
      title="My Favorites"
      subtitle="Studios and instructors you've saved"
      tabs
    />
  ),
  loader: async ({ context }) => {
    // On client-side navigations this goes browser → Go API directly; only
    // SSR pays the cookie-forwarding proxy hop (see authenticated-shared).
    // The nav card on the profile hub data-prefetches this exact query
    // (favouritesQuery from @/lib/queries/detail), so a hub→favourites tap
    // usually lands on a primed cache.
    const refresh = () => context.queryClient.ensureQueryData(favouritesQuery());
    // Cache-first gate: re-entry (e.g. back from a detail page after a
    // favourite toggle) resolves from the cached list instantly while the
    // background refresh heals staleness; a cold cache keeps the streamed
    // fetch the Await boundary renders against.
    const { cached, stream } = cacheFirst(
      context.queryClient,
      ['v2-favorites', 'instructors,studios'],
      refresh
    );
    return { favoritesPromise: cached !== undefined ? Promise.resolve(cached) : stream() };
  },
  validateSearch: (input: Record<string, unknown>) => ({
    tab: input.tab === 'instructors' ? 'instructors' : 'studios',
  }),
});
